import { type ChangeEvent, type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { Button, Control } from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";

import styles from "../WebLensPage.module.css";
import { LogoPlaceholder, messages } from "../messages.generated";

interface WebLensIconControlProps {
    logo: File | null;
    disabled: boolean;
    /** Called with every accepted pick — callers reset their checklist here (see dropzone note). */
    onPick: (logo: File) => void;
    /**
     * Icon the lens keeps when no replacement is attached (the update page). A .lens-built
     * predecessor's icon lives inside its package rather than in a stored object, so it may
     * render here yet still fall back to the default after conversion — the caller's hint
     * should say as much. Absent (the submit page), the PNG placeholder shows instead.
     */
    currentIconUrl?: string;
    cta: ReactNode;
    hint: ReactNode;
}

/**
 * The lens-icon control shared by the submit and update pages: a tile showing what the lens will
 * end up with — the attached replacement, else the icon it keeps, else the placeholder — plus the
 * pick button and hidden input. Deliberately attach-only, matching both pages: re-picking corrects
 * a wrong file, and leaving the control alone keeps the current behavior (a paired un-pick button
 * tested as confusing).
 */
export const WebLensIconControl: FC<WebLensIconControlProps> = ({
    logo,
    disabled,
    onPick,
    currentIconUrl,
    cta,
    hint,
}) => {
    const intl = useIntl();
    const logoInputRef = useRef<HTMLInputElement>(null);

    // createObjectURL is a side effect, so it belongs in useEffect (paired with its revoke), not
    // useMemo — an aborted/discarded render would otherwise leak the blob URL with no cleanup.
    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!logo) {
            setLogoPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(logo);
        setLogoPreviewUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [logo]);

    const onLogoPicked = (event: ChangeEvent<HTMLInputElement>) => {
        const picked = event.target.files?.[0];
        if (picked && !disabled) {
            onPick(picked);
        }
        // Clear so re-selecting the same (locally modified) logo still fires onChange.
        event.target.value = "";
    };

    return (
        <Control label={intl.formatMessage(messages.logoLabel)}>
            <div className={styles.logoRow}>
                {logoPreviewUrl ? (
                    <img
                        className={styles.logoPreview}
                        src={logoPreviewUrl}
                        alt={intl.formatMessage(messages.logoPreviewAlt)}
                    />
                ) : currentIconUrl ? (
                    <img
                        className={styles.logoPreview}
                        src={currentIconUrl}
                        alt={intl.formatMessage(messages.currentLogoAlt)}
                    />
                ) : (
                    <div className={styles.logoPlaceholder}>
                        <LogoPlaceholder />
                    </div>
                )}
                <Button
                    type="default"
                    size="small"
                    isDisabled={disabled}
                    onClick={() => {
                        logoInputRef.current?.click();
                    }}
                    data-testid="web-lens-upload-logo"
                >
                    {cta}
                </Button>
                <input
                    ref={logoInputRef}
                    className={styles.fileInput}
                    type="file"
                    accept="image/png"
                    onChange={onLogoPicked}
                />
            </div>
            <div className={styles.fieldHint}>{hint}</div>
        </Control>
    );
};
